import * as Sentry from "@sentry/remix";
import { DynamicLinks } from "~/utils/links";
import { GTagEvent } from "~/utils/gTagEvent";
import { useEffect, useContext } from "react";
import {
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  isRouteErrorResponse,
  useLoaderData,
  useRouteError,
} from "@remix-run/react";
import { StructuredData } from "~/utils/structuredData";
import { Global, withEmotionCache } from "@emotion/react";
import { ChakraProvider, extendTheme } from "@chakra-ui/react";
import "@fontsource-variable/roboto-flex/wght.css";
import CookieConsent from "./cookieconsent.umd.js?raw";
import "./cookieconsent.css";

import {
  ServerStyleContext,
  ClientStyleContext,
  ServerStyleContextData,
} from "./context";

declare global {
  interface Window {
    gtag: (a: unknown, b: unknown, c: unknown) => void;
    CookieConsent?: {
      run: (config: object) => void;
    };
  }
}

const theme = extendTheme({
  components: {
    Divider: {
      baseStyle: {
        borderColor: "blackAlpha.400",
      },
    },
    Button: {
      baseStyle: {
        borderRadius: 0, // <-- border radius is same for all variants and sizes
      },
      variants: {
        outline: {
          backgroundColor: "white",
        },
      },
      defaultProps: {
        backgroundColor: "white",
        colorScheme: "primary", // default is gray
      },
    },
  },
  colors: {
    primary: {
      100: "#D5622E",
      200: "#D5622E",
      300: "#D5622E",
      400: "#D5622E",
      500: "#D5622E",
      600: "#D5622E",
      700: "#D5622E",
      800: "#D5622E",
      900: "#D5622E",
    },
    secondary: "#8CAAA0",
    creamy: "#F3ECE6",
    blacky: "#212121",
  },
  fonts: {
    heading: `'Sora', sans-serif;`,
    body: `'Sora', sans-serif;`,
  },
  styles: {
    global: (/*props: StyleFunctionProps*/) => ({
      html: {
        height: "100%",
      },
      body: {
        margin: 0,
        padding: 0,
        minHeight: "100%",
        backgroundColor: "white",
      },
    }),
  },
});

const Fonts = () => {
  return (
    <Global
      styles={`
      @font-face {
       font-family: 'Nohemi';
       src: url(/fonts/Nohemi/Web-TT/Nohemi-Regular.woff2) format('woff2'),
       src: url(/fonts/Nohemi/Web-TT/Nohemi-Regular.woff) format('woff'),
            url(/fonts/Nohemi/Web-TT/Nohemi-Regular.woff2) format('ttf');
      }
      @font-face {
       font-family: 'Sora';
       src: url(/fonts/Sora/static/Sora-Regular.ttf) format('ttf');
      }
      body {
        background-image: linear-gradient(to top, #dfe9f3 0%, white 85%);
        background-attachment: fixed;
        background-position: center;
      }
    `}
    />
  );
};

export async function loader() {
  return {
    env: {
      g_tag: process.env.G_TAG,
      g_tag_ads: process.env.G_TAG_ADS,
      sentry_dsn: process.env.SENTRY_DSN,
    },
  };
}

function App({
  serverStyleData,
}: {
  serverStyleData: ServerStyleContextData[] | null;
}) {
  const { env } = useLoaderData<typeof loader>();
  const ENV = { SENTRY_DSN: env.sentry_dsn };

  useEffect(() => {
    cookieConsentRun();
    document.body.addEventListener(
      "error",
      (event) => {
        if (!event.target) return;
        const target = event.target as HTMLImageElement | HTMLLinkElement;

        if (target instanceof HTMLImageElement) {
          Sentry.captureException(
            new Error(`Failed to load image: ${target.src}`)
          );
        } else if (target instanceof HTMLLinkElement) {
          Sentry.captureException(
            new Error(`Failed to load css: ${target.href}`)
          );
        }
      },
      true // useCapture - necessary for resource loading errors
    );
  }, []);

  return (
    <html lang="pl">
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <script
          dangerouslySetInnerHTML={{
            __html: `window.ENV = ${JSON.stringify(ENV)}`,
          }}
        ></script>
        <Meta />
        <StructuredData />
        <Links />
        <DynamicLinks />
        {serverStyleData?.map(({ key, ids, css }) => (
          <style
            key={key}
            data-emotion={`${key} ${ids.join(" ")}`}
            dangerouslySetInnerHTML={{ __html: css }}
          />
        ))}
      </head>
      <body>
        {env.g_tag && (
          <script
            type="text/plain"
            data-category="analytics"
            data-service="ga"
            async={true}
            src={`https://www.googletagmanager.com/gtag/js?id=${env.g_tag}`}
          ></script>
        )}
        {env.g_tag && (
          <script
            type="text/plain"
            data-category="analytics"
            data-service="ga"
            dangerouslySetInnerHTML={{
              __html: `
                window.dataLayer = window.dataLayer || [];
                window.gtag = function (){dataLayer.push(arguments);}
                gtag('js', new Date());
                gtag('config', '${env.g_tag}');
                gtag('config', '${env.g_tag_ads}');
              `,
            }}
          />
        )}
        <GTagEvent />
        <script
          defer
          dangerouslySetInnerHTML={{
            __html: CookieConsent,
          }}
        />
        <ChakraProvider theme={theme}>
          <Fonts />
          <Outlet />
        </ChakraProvider>
        <ScrollRestoration />
        <Scripts />
      </body>
    </html>
  );
}

export default withEmotionCache((_, emotionCache) => {
  const serverStyleData = useContext(ServerStyleContext);
  const clientStyleData = useContext(ClientStyleContext);

  // Only executed on client
  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    // re-link sheet container
    emotionCache.sheet.container = document.head;
    // re-inject tags
    const tags = emotionCache.sheet.tags;
    emotionCache.sheet.flush();

    tags.forEach((tag) => {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-expect-error
      (emotionCache.sheet as unknown)._insertTag(tag);
    });
    // reset cache to reapply global styles
    clientStyleData?.reset();
  }, []);

  return <App serverStyleData={serverStyleData} />;
});

export function ErrorBoundary() {
  const error = useRouteError();
  return (
    <html>
      <head>
        <title>Oops!</title>
        <Meta />
        <Links />
      </head>
      <body>
        <h1>
          {isRouteErrorResponse(error)
            ? `${error.status} ${error.statusText}`
            : error instanceof Error
            ? error.message
            : "Unknown Error"}
        </h1>
        <Scripts />
      </body>
    </html>
  );
}

function cookieConsentRun() {
  if (window?.CookieConsent) {
    window?.CookieConsent.run({
      guiOptions: {
        consentModal: {
          layout: "box",
          position: "bottom left",
          equalWeightButtons: true,
          flipButtons: false,
        },
        preferencesModal: {
          layout: "box",
          position: "right",
          equalWeightButtons: true,
          flipButtons: false,
        },
      },
      categories: {
        analytics: {
          autoClear: {
            cookies: [
              {
                name: /^(_ga|_gid)/, //regex
              },
            ],
          },
          services: {
            ga: {
              label: "Google Analytics",
              cookies: [
                {
                  name: /^(_ga|_gid)/,
                },
              ],
            },
          },
        },
      },
      language: {
        default: "en",
        translations: {
          en: {
            consentModal: {
              title: "Używamy plików cookie 🍪🍪🍪",
              description: `
                Nasza strona internetowa korzysta z plików cookie aby <b>zapewnić poprawność działania procesu zakupowego</b> oraz aby zapewnić jak najlepsze wrażenia podczas przeglądania, analizować ruch na stronie oraz dostosowywać treści do Twoich.
                <br/></br>Klikając „Akceptuję”, wyrażasz zgodę na używanie wszystkich plików cookie. Możesz również dostosować swoje preferencje, wybierając „Ustawienia plików cookie”. Więcej informacji znajdziesz w naszej <a href="/polityka-prywatnosci">Polityce prywatności</a>.
                `,
              acceptAllBtn: "✅ Akceptuję",
              acceptNecessaryBtn: "❌ Odrzucam wszystkie",
              showPreferencesBtn: "⚙️ Ustawienia plików cookie",
              footer: "",
            },
            preferencesModal: {
              title: "Consent Preferences Center",
              acceptAllBtn: "Accept all",
              acceptNecessaryBtn: "Reject all",
              savePreferencesBtn: "Save preferences",
              closeIconLabel: "Close modal",
              serviceCounterLabel: "Service|Services",
              sections: [
                {
                  title: "Użycie ciasteczek",
                  description: `
                    Gdy odwiedzasz dowolną stronę internetową, może ona przechowywać lub pobierać informacje z Twojej przeglądarki, głównie w formie plików cookies. Informacje te mogą dotyczyć Ciebie, Twoich preferencji lub Twojego urządzenia i są zazwyczaj wykorzystywane do zapewnienia działania strony zgodnie z Twoimi oczekiwaniami. Informacje te zazwyczaj nie identyfikują Cię bezpośrednio, ale mogą zapewnić bardziej spersonalizowane doświadczenie w sieci.
                    <br/>Szanując Twoje prawo do prywatności, możesz zdecydować, aby nie zezwalać na niektóre rodzaje plików cookies.`,
                },
                {
                  title:
                    'Wymagane <span class="pm__badge">Zawsze włączone</span>',
                  description:
                    "Ciasteczka są potrzebne do prawidłowego działania procesu autoryzacji oraz procesu zakupowego.",
                  linkedCategory: "necessary",
                },
                {
                  title: "Analityka",
                  description: "",
                  linkedCategory: "analytics",
                },
              ],
            },
          },
        },
      },
    });
  }
}
